import type { HelpContent } from './types';

export const budgetVarianceHelp: HelpContent = {
  pageTitle: 'Budget Variance Report',
  description: 'Analyse budget vs actual spend across projects and cost categories, with variance indicators to highlight where budgets are under pressure.',

  functionality: {
    overview: 'The Budget Variance Report shows how actual spend compares to budget across your project portfolio. It breaks down variance by cost category, helping you identify where budgets are being exceeded and where underspend may indicate delivery risk.',

    keyFeatures: [
      {
        label: 'Portfolio variance summary',
        detail: 'KPI cards show total budget, total actual spend, overall variance, and the number of projects over budget at a glance.',
      },
      {
        label: 'Category-level breakdown',
        detail: 'Drill into variance by cost category — such as labour, expenses, and purchases — to understand where the budget pressure is coming from.',
      },
      {
        label: 'Health-based variance indicators',
        detail: 'Each project and category is colour-coded based on configurable budget health thresholds, making it easy to spot where spend is approaching or exceeding budget.',
      },
      {
        label: 'Expandable project rows',
        detail: 'Expand any project row to see a category-level breakdown of budget vs actual, giving you the detail behind the headline numbers.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and filter by budget health status to focus on projects that need attention.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the full budget variance report to Excel, including category-level detail.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays total budget, total actuals, overall variance, and count of over-budget projects across the portfolio.',
      },
      {
        name: 'Search bar',
        description: 'Filter the project list in real time by project name or client name.',
      },
      {
        name: 'Budget health filter',
        description: 'Filter projects by their budget health status — On Track, At Risk, or Over Budget.',
      },
      {
        name: 'Budget variance table',
        description: 'Lists all projects with columns for project name, client, total budget, total actuals, variance value, variance percentage, and budget health status.',
      },
      {
        name: 'Expandable category rows',
        description: 'Click the expand icon on a project row to reveal a cost category breakdown, showing budget, actuals, and variance for each category.',
      },
      {
        name: 'Variance indicator',
        description: 'A colour-coded indicator showing whether the project is On Track (green), At Risk (amber), or Over Budget (red) based on configured thresholds.',
      },
      {
        name: 'Export button',
        description: 'Downloads the budget variance report as an Excel file, including expanded category detail.',
      },
    ],

    useCases: [
      'Reviewing budget performance across the portfolio before a finance review.',
      'Identifying projects that are over budget or approaching their budget threshold.',
      'Understanding which cost categories are driving overspend on a specific project.',
      'Exporting variance data for use in finance reports or board packs.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Budget Variance Report loads with all projects that have budget data. Use the KPI cards for a portfolio summary, then expand individual project rows to see cost category detail.',
    steps: [
      {
        title: 'How do I see the category breakdown for a project?',
        items: [
          { text: 'Find the project in the table.' },
          { text: 'Click the expand icon on the left of the project row.' },
          { text: 'The row expands to show budget, actuals, and variance for each cost category.' },
        ],
      },
      {
        title: 'How do I filter to over-budget projects?',
        items: [
          { text: 'Use the budget health filter and select "Over Budget".' },
          { text: 'The table will update to show only projects that have exceeded their budget threshold.' },
        ],
      },
      {
        title: 'How do I export the report?',
        items: [
          { text: 'Apply any filters you need.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file including category-level detail.' },
        ],
      },
    ],
    proTips: [
      'The budget health thresholds that determine On Track, At Risk, and Over Budget can be configured in Settings. If the thresholds don\'t reflect your organisation\'s risk appetite, ask your Administrator to adjust them.',
      'Expand all project rows before exporting to ensure the full category-level detail is included in your Excel file.',
    ],
  },

  faq: [
    {
      question: 'How are the budget health thresholds set?',
      answer: 'Budget health thresholds are configured by an Administrator in Settings. They define the percentage of budget consumed at which a project moves from On Track to At Risk, and from At Risk to Over Budget.',
    },
    {
      question: 'Why does a project show no variance data?',
      answer: 'A project will only show variance data if a budget has been set and actual spend has been recorded. If either is missing, the project may show blank variance columns.',
    },
    {
      question: 'Can I edit budget figures from this report?',
      answer: 'No. The Budget Variance Report is read-only. To update budget figures, navigate to the relevant project\'s budget tab in Project Details.',
    },
    {
      question: 'What cost categories are shown in the breakdown?',
      answer: 'The categories shown reflect the cost categories configured for your organisation. These are managed by an Administrator in Settings.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Project Status Report', path: '/reports/project-status' },
    { label: 'Profitability Report', path: '/reports/profitability' },
  ],
};
